// VideoPlayerPage.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchVideoClips } from "../../features/videoClip/videoClipSlice";
import { VideoInfoPanel } from "./VideoInfoPanel";
import { MainVideoPlayer } from "./MainVideoPlayer";
import { ClipList } from "./ClipList";

export default function VideoPlayerPage({
  video = null,
  profile = null,
  allVideos = [],
  onClose = null
}) {
  const dispatch = useDispatch();
  const { clips, loading: clipsLoading } = useSelector((state) => state.videoClip);
console.log(clips,"clips in video player page");
  // Use passed video or fallback to demo data
  const initialVideo = video || {
    _id: 1,
    videoUrl: "/videos/demo.mp4",
    title: "Demo Video",
    description: "Sample video description"
  };

  // Helper function to get video URL from different possible field names
  const getVideoUrl = (vid) => {
    return vid?.videoUrl || vid?.url || vid?.src || vid?.video || vid?.file || null;
  };

  const [activeClip, setActiveClip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentVideoInfo, setCurrentVideoInfo] = useState(video);

  // Main video object - based on currentVideoInfo
  const mainVideo = currentVideoInfo ? {
    id: currentVideoInfo._id,
    title: currentVideoInfo.title || "FULL VIDEO",
    duration: currentVideoInfo.duration || "00:00",
    src: getVideoUrl(currentVideoInfo),
    isMainVideo: true // Flag to identify main video
  } : { id: 1, title: "Demo Video", duration: "00:00", src: "/videos/demo.mp4", isMainVideo: true };

  // Other videos from the profile - dynamically filter based on currently playing video
  const otherVideos = allVideos
    .filter(v => v._id !== currentVideoInfo?._id)
    .map(v => ({
      id: v._id,
      date: new Date(v.uploadedAt || v.createdAt || Date.now()).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }),
      thumbnail: v.thumbnailUrl || v.thumbnail,
      title: v.title || "Video",
      src: getVideoUrl(v)
    }));

  // Fetch clips from backend when current video or profile changes
  useEffect(() => {
    if (currentVideoInfo?._id && (profile?._id || profile?.playerId)) {
      dispatch(fetchVideoClips({
        videoId: currentVideoInfo._id,
        playerId: profile._id || profile.playerId,
        page: 1,
        limit: 50
      }));
    }
  }, [currentVideoInfo?._id, profile?._id, profile?.playerId, dispatch]);

  // Update current video info when video prop changes
  useEffect(() => {
    if (video) {
      setCurrentVideoInfo(video);
    }
  }, [video]);

  // Set activeClip to mainVideo when currentVideoInfo changes (new video selected)
  useEffect(() => {
    if (mainVideo) {
      setActiveClip(mainVideo);
    }
  }, [currentVideoInfo?._id]);

  // Format clips from backend - only saved clips (no main video)
  const formatClips = () => {
    if (!clips || clips.length === 0) return [];

    return clips.map((clip) => ({
      id: clip.clipId || clip._id,
      clipId: clip.clipId || clip._id,
      duration: clip.duration, // Pass raw milliseconds to ClipList for formatting
      src: clip.clipUrl || clip.video?.videoUrl || clip.url, // Use video.videoUrl if clipUrl is null
      inTime: clip.inTime,
      outTime: clip.outTime,
      description: clip.description || "",
      status: clip.status,
      isMainVideo: false
    }));
  };

  const allClips = formatClips();

  // Debug: Log video information
  useEffect(() => {
    console.log("VideoPlayerPage - Video:", video);
    console.log("VideoPlayerPage - Active Clip:", activeClip);
    console.log("VideoPlayerPage - Video URL:", activeClip?.src);
    console.log("VideoPlayerPage - Current Video Info:", currentVideoInfo);
    console.log("VideoPlayerPage - Fetched Clips from Backend:", clips);
    console.log("VideoPlayerPage - All Clips (formatted):", allClips);
  }, [video, activeClip, currentVideoInfo, clips, allClips]);

  const handleSelectClip = (clip) => {
    setLoading(true);
    setActiveClip(clip);
    setTimeout(() => setLoading(false), 300);
  };

  const handleSelectOtherVideo = (selectedVideo) => {
    console.log("Selected other video", selectedVideo);

    // Find the full video object from allVideos
    const fullVideoData = allVideos.find(v => v._id === selectedVideo.id);

    if (fullVideoData) {
      // Update current video info to show the new video's details
      setCurrentVideoInfo(fullVideoData);

      // Create a new clip object from the selected video
      const newClip = {
        id: fullVideoData._id,
        title: fullVideoData.title || "VIDEO",
        duration: fullVideoData.duration || "00:00",
        src: getVideoUrl(fullVideoData)
      };

      // Update the active clip to play the new video
      setLoading(true);
      setActiveClip(newClip);

      // Scroll to top to see the video player
      window.scrollTo({ top: 0, behavior: 'smooth' });

      setTimeout(() => setLoading(false), 300);
    }
  };

  const playerName = profile
    ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
    : 'Player';

  return (
    <div className="min-h-screen bg-jp-section text-jp-ink px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pt-20 sm:pt-24">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div />
        {onClose && (
          <button
            onClick={onClose}
            className="text-jp-muted hover:text-jp-ink flex items-center gap-2 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="text-sm">Close</span>
          </button>
        )}
      </div>

      {/* Responsive Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[330px_minmax(0,1fr)_280px] gap-4 sm:gap-6 items-start">

        {/* Left Panel - Info (Hidden on mobile, shown on lg+) */}
        <div className="hidden lg:block order-1">
          <VideoInfoPanel
            title={currentVideoInfo?.title || "Video"}
            position={profile?.primaryPosition || profile?.position || "Player"}
            roleText={profile?.description || ""}
            strengths={profile?.strengths?.join(', ') || ""}
            description={currentVideoInfo?.description || "No description available"}
            otherVideos={otherVideos}
            onSelectOther={handleSelectOtherVideo}
          />
        </div>

        {/* Center - Video Player */}
        <div className="order-2 lg:order-2">
          <MainVideoPlayer
            src={activeClip?.src}
            isLoading={loading}
            poster={currentVideoInfo?.thumbnailUrl || currentVideoInfo?.thumbnail}
            videoId={currentVideoInfo?._id}
            playerId={profile?._id || profile?.playerId}
            isMainVideo={activeClip?.isMainVideo}
          />

          {/* Mobile Info Panel - Shows below video on mobile */}
          <div className="lg:hidden mt-4">
            <VideoInfoPanel
              title={currentVideoInfo?.title || "Video"}
              position={profile?.primaryPosition || profile?.position || "Player"}
              roleText={profile?.description || ""}
              strengths={profile?.strengths?.join(', ') || ""}
              description={currentVideoInfo?.description || "No description available"}
              otherVideos={otherVideos}
              onSelectOther={handleSelectOtherVideo}
            />
          </div>
        </div>

        {/* Right Panel - Clips (Hidden on mobile and tablet, shown on xl+) */}
        <div className="hidden xl:block order-3">
          <ClipList
            clips={allClips}
            activeClipId={activeClip?.id}
            onSelectClip={handleSelectClip}
            loading={clipsLoading}
          />
        </div>

      </div>

      {/* Mobile/Tablet Clips - Shows below everything on smaller screens */}
      <div className="xl:hidden mt-6">
        <ClipList
          clips={allClips}
          activeClipId={activeClip?.id}
          onSelectClip={handleSelectClip}
          loading={clipsLoading}
        />
      </div>
    </div>
  );
}
